<template>
  <div>
    <section class="intro bg-grey center-div"
             style="height: 460px; background-size: cover; background-image: url('/assets/images/faqs/CitSci_20180923_1134-X3-2.PNG');">
      <div class='intro-description text-white' data-aos="zoom-in" data-aos-duration="1500" data-aos-delay='250'>
        <div class="container">
          <br/>
          <p class="intro-description-text text-white font-weight-black text-center" style="font-size: 60px">Frequently
            Asked Questions</p>
        </div>
      </div>
    </section>

    <div class="container" data-aos="fade-up">
      <div data-aos="fade-up">
        <h1 class="font-weight-bold text-left">About CitSci</h1>
        <h3>What is CitSci?</h3>
        <p>CitSci is a free online platform and global community that supports citizen and community science projects.
          We provide tools for collecting, managing, visualizing, and sharing data-so anyone, anywhere, can do great
          science.
        </p>
        <h3>Who uses CitSci?</h3>
        <p>People using CitSci include:</p>
        <p>
          <ul>
            <li>Scientists and researchers</li>
            <li>Community groups and nonprofits</li>
            <li>Educators and students (K-12 through university)</li>
            <li>Government agencies</li>
            <li>People from all backgrounds, of all ages, curious about their world</li>
          </ul>
        </p>
        <h3>What is citizen science?</h3>
        <p>As our
          <b-link href="https://scistarter.org/faq" target="NewWindow">friends at SciStarter</b-link>
          say, “citizen science is science...
          If you are a citizen scientist, you [may not] have a formal science degree although you do engage in data
          collection,
          data analysis, and other steps of the scientific process.” People use different terms for citizen science:
          participatory
          science, neighborhood science, crowdsourced science, public science, crowd science, and more. Regardless,
          we're here to support
          <i>everyone</i> participating in real science: building projects, collecting and analyzing data, and
          leveraging results for decision making.
        </p>
      </div>
      <div data-aos="fade-up" class="pt-4">
        <hr>
        <h1 class="font-weight-bold text-left">Getting Started</h1>
        <h3>How do I create an account?</h3>
        <p>Click “<b-link href="/register">Sign Up</b-link>”. You'll just need an email address and a password to get started. Need help? Watch this
          <IconLink href="https://www.youtube.com/watch?v=wW4X93gabqI&t=1s">tutorial</IconLink>
          or
          <IconLink href="https://blog.citsci.org/2020/10/09/how-to-create-a-citsci-account/">read our setup blog</IconLink>.
        </p>
        <h3>What's the difference between the CitSci Mobile App and the CitSci Website?</h3>
        <p>
          Both the CitSci Mobile App and CitSci Website are designed to help you participate in and manage citizen science
          projects - but they're optimized for different tasks.In short, if you're a participant, you typically
          find the project you want on the website and then use the app to submit data from the field, and if
          you're a project leader, you mainly use the website to create projects, explore results, and communicate
          with participants:
        </p>
        <h4><i>CitSci Website (CitSci.org)</i></h4>
        <p>
          <ul>
            <li>On the participant side, best for choosing what project to join (able to easily read all project info)
              and for reviewing project resources and results.
            </li>
            <li>On the project leader side, best for project creation, customization, and data analysis.</li>
            <ul>
              <li>Use the website when you're setting up your project, managing data, or analyzing trends</li>
              <li>Full access to tools for:</li>
              <ul>
                <li>Creating datasheets</li>
                <li>Managing volunteers</li>
                <li>Viewing and analyzing results (charts, maps, stats)</li>
                <li>Exporting data</li>
              </ul>
            </ul>
            <li>Overall, the website is ideal for project managers, participants, educators, and researchers who
              need to configure projects or dive into more complex datasets
            </li>
          </ul>
        </p>
        <h4><i>CitSci Mobile App</i></h4>
        <p>
          <ul>
            <li>Available for
              <IconLink href="https://apps.apple.com/us/app/citsci/id657196305">iOS</IconLink>
              and
              <IconLink href="https://play.google.com/store/apps/details?id=com.nrel.citsci&hl=en_US&gl=US">Android</IconLink>
            </li>
            <li>Ideal for collecting data in the field</li>
            <li>Works offline-store data on your device and upload later</li>
            <li>Quick and easy for volunteers to log observations like photos, locations, counts, or notes on the go
            </li>
            <li>Designed for on-the-ground participation in projects</li>
            <li>Use the app when you're outside collecting data-like snapping pictures of invasive species, recording
              water quality, or noting weather conditions
            </li>
          </ul>
        </p>
        <h3>What types of projects can I create?</h3>
        <p>CitSci supports a wide range of projects-from quick surveys to long-term monitoring studies that involve
          multiple pictures and measurements. You can customize your data forms, message volunteers, and collect
          data on everything from air quality to amphibians to neighborhood heat.
        </p>
        <h3>How do I start a project?</h3>
        <p>After logging in, the "<b-link href="/projects/create">Create a Project</b-link>
          " page walks you through the process to:
        </p>
        <p>
          <ul>
            <li>Upload your logo and banner</li>
            <li>Customize your datasheets</li>
            <li>Set privacy and participation preferences</li>
            <!--<li>Check out our “Quick Guide to Starting a Project” blog post for a step-by-step walkthrough</li>-->
          </ul>
        </p>
      </div>
      <div data-aos="fade-up" class="pt-4">
        <hr>
        <h1 class="font-weight-bold text-left">Platform Features</h1>
        <h3>Can I collect both numbers and images?</h3>
        <p>Yes. CitSci supports:</p>
        <p>
          <ul>
            <li>Quantitative data (e.g., water pH, bird counts)</li>
            <li>Qualitative data (e.g., notes, photos, comments)</li>
          </ul>
        </p>
        <p>Our datasheet creator includes text, number, dropdown, image, date/time, and location fields.</p>
        <h3>Can I create multiple datasheets per project?</h3>
        <p>Yes! You can create as many datasheets as you need within one project-for different protocols, timeframes, or
          participants.</p>
        <h3>Can I control who can see or join my project?</h3>
        <p>Definitely. You control:</p>
        <p>
          <ul>
            <li>Membership: Open (anyone can join) or Member-Based (requires approval)</li>
            <li>Data visibility (at both the project and datasheet level): Public (visible to all) or
              Private (only visible to members)
            </li>
          </ul>
        </p>
        <h3>What location formats are supported?</h3>
        <p>As a project manager you can:</p>
        <p>
          <ul>
            <li>Let participants select latitude/longitude for opportunistic sampling</li>
            <li>Provide a list of pre-defined locations for participants (for example, "Rock Creek Park in Washington
              DC," etc)
            </li>
            <li>Or both</li>
          </ul>
        </p>
        <p>There are also options for you to assign locations to specific volunteers.</p>
        <p>
          <b-link to="contact">Contact Us</b-link> if you want help
          deciding which location format is right for you.
        </p>

      </div>
      <div data-aos="fade-up" class="pt-4">
        <hr>
        <h1 class="font-weight-bold text-left">Impact & Community</h1>
        <h3>How many projects use CitSci?</h3>
        <p>Over 1,400 projects with more than 18,000 contributors have used CitSci since 2007. Projects range from
          hyper-local efforts to global data collection initiatives.</p>
        <h3>What's the impact of CitSci projects?</h3>
        <p>CitSci has supported:</p>
        <p>
          <ul>
            <li>Community-led data influencing local policy</li>
            <li>
              <b-link :to="'publications'">Peer-reviewed research</b-link>
            </li>
            <li>Cross-cultural and Indigenous science collaborations</li>
            <li>Data for national agencies, communities, and conservation strategy</li>
          </ul>
        </p>
        <p>Check out our
          <b-link :to="'impact'">Impact page</b-link>
          for inspiring examples.
        </p>
        <h3>How can I keep up with what's new?</h3>
        <p>Follow us on
          <b-link href="https://instagram.com/citsci_org" target="NewWindow">Instagram</b-link>,
          <b-link href="https://www.facebook.com/pages/CitSciorg/172382202839106" target="NewWindow">Facebook</b-link>, and
          <b-link href="https://www.linkedin.com/company/citsci-org/" target="NewWindow">LinkedIn</b-link>.
          Subscribe to our
          <b-link to="newsletter">newsletter</b-link>, or read the latest stories on our
          <IconLink href="https://blog.citsci.org/">blog</IconLink>
        </p>
      </div>
      <div data-aos="fade-up" class="pt-4">
        <hr>
        <h1 class="font-weight-bold text-left">Support & Contacts</h1>
        <h3>Is CitSci really free?</h3>
        <p>Yes! Thanks to generous donors and grant funding, our core tools are free to use
          (and they actually provide everything you need to run a project). Want to keep it that way?
          <b-link :to="'donate'">Donate today</b-link>
          . Your donations help support students and staff that work with us.
        </p>
        <h3>Do you offer consulting or tech support?</h3>
        <p>We offer both
          <b-link :to="'services'">free and paid services</b-link>
          depending on your needs.
          In other words, you can pay us if you need extra help with technology development or services to facilitate
          your project.
          We can help with:
        </p>
        <p>
          <ul>
            <li>Project setup and training (webinars, in-person presentations, etc)</li>
            <li>Content development (one-pagers, slide decks, etc)</li>
            <li>Volunteer recruitment and retention</li>
            <li>Custom software features</li>
            <li>Data workflows and integrations</li>
          </ul>
        </p>
        <h3>Get in touch,
          <b-link to="contact">Contact Us</b-link>
        </h3>
      </div>
    </div>
  </div>
</template>

<script>
import IconLink from "../layout/IconLink.vue";

export default {
  name: 'FAQs',
  components: {
    IconLink,
  },
}
</script>

<style lang="scss" scoped>

hr {
  border-color: grey;
}

</style>
